import React, { useEffect, useState } from 'react';
import PdfViewerModal from './PdfViewerModal';

/**
 * Visionneuse PDF GLOBALE de l'app.
 *
 * Monté une seule fois (dans index.js). Écoute l'événement `pfm:open-pdf` émis
 * par utils/pdfMobile.js : sur mobile (webview Capacitor), toute ouverture de PDF
 * via window.open(blob/dataURL) — cassée nativement — est redirigée ici et
 * affichée dans la MÊME visionneuse in-app que la remise de propriété (aperçu
 * pdf.js plein écran + boutons « Partager / Imprimer » et « Fermer »).
 *
 * detail attendu : { base64: string (dataURL ou base64 brut), filename?: string }.
 * Plusieurs événements successifs sont EMPILÉS : les documents s'affichent l'un
 * après l'autre (fermer celui du dessus révèle le suivant).
 *
 * Sur desktop / navigateur web : l'intercepteur n'est pas installé, aucun
 * événement n'est émis, ce composant reste inerte (window.open ouvre l'onglet).
 */
export default function GlobalPdfViewer() {
  // FILE D'ATTENTE (et non un seul PDF) : quand plusieurs documents sont
  // demandés d'un coup — ex. « Imprimer la facture » ET « Imprimer la feuille
  // de temps » à la validation d'une intervention — chaque événement empilait
  // et ÉCRASAIT le précédent : un seul document s'affichait. Ils défilent
  // désormais l'un après l'autre, à la fermeture de chacun.
  const [file, setFile] = useState([]); // [{ id, base64, filename }]

  useEffect(() => {
    let compteur = 0;
    const handler = (e) => {
      const detail = e && e.detail ? e.detail : {};
      if (!detail.base64) return;
      compteur += 1;
      const identifiant = `pdf-${compteur}`;
      setFile((precedents) => {
        // Garde anti-boucle : le bouton « Partager » de la visionneuse peut,
        // sur certaines webviews, retomber sur window.open(blob) — que le shim
        // pdfMobile réintercepte et réémet. Sans ce test, le MÊME document
        // s'empilerait à chaque appui, sans limite.
        if (precedents.length && precedents[0].base64 === detail.base64) return precedents;
        return [...precedents, {
          id: identifiant,
          base64: detail.base64,
          filename: detail.filename || 'document.pdf',
        }];
      });
    };
    window.addEventListener('pfm:open-pdf', handler);
    return () => window.removeEventListener('pfm:open-pdf', handler);
  }, []);

  if (file.length === 0) return null;
  const courant = file[0];

  return (
    <PdfViewerModal
      // `key` : force le remontage au passage au document suivant (sinon
      // pdf.js garderait le rendu du précédent).
      key={courant.id}
      base64={courant.base64}
      filename={courant.filename}
      onClose={() => setFile((precedents) => precedents.slice(1))}
    />
  );
}
